import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { RefreshCw, Shield } from 'lucide-react';
import { INCIDENTS_API } from '@/lib/incidentsApi';
import { normalizeTicketsListPayload, TICKETS_PAGE_LIMIT } from '@/lib/ticketsApi';
import {
  getWorkflowStageLabel,
  isPendingApproval,
  WORKFLOW_STAGE,
  WORKFLOW_STAGE_LABELS,
} from '@/lib/workflowHelpers';

const API = INCIDENTS_API;
const POLL_MS = 6000;

function stageBadgeClass(stage, ticket) {
  const label = String(
    (ticket && getWorkflowStageLabel(ticket)) || WORKFLOW_STAGE_LABELS[stage] || '',
  ).toLowerCase();
  // End-user reported access issue — critical red.
  if (label.includes('access issue') || label.includes('sap login issue')) {
    return 'bg-cp-crit text-white';
  }
  // Soft FFID holds — yellow/amber.
  if (
    label.includes('human escalation') ||
    label.includes('firefighter already assigned') ||
    label.includes('no firefighter id available') ||
    label.includes('form missing') ||
    label.includes('invalid access window')
  ) {
    return 'bg-cp-mod text-white';
  }
  switch (stage) {
    case WORKFLOW_STAGE.PENDING_APPROVAL:
      return 'bg-cp-imp text-white';
    case WORKFLOW_STAGE.NEEDS_INFO:
      return 'bg-cp-mod text-white';
    case WORKFLOW_STAGE.DONE:
      return 'bg-cp-ok text-white';
    case WORKFLOW_STAGE.SCHEDULED:
      return 'bg-cp-mod text-white';
    case WORKFLOW_STAGE.ACCESS_ISSUE_REPORTED:
      return 'bg-cp-crit text-white';
    case WORKFLOW_STAGE.REJECTED:
    case WORKFLOW_STAGE.FAILED:
      return 'bg-cp-crit text-white';
    default:
      return 'bg-cp-violet text-white';
  }
}

/**
 * Dedicated FFID operator queue — Gateway/FE gated by ffid.update.
 * Uses GET /ffid (not the generic tickets list).
 */
export default function FfidTickets() {
  const navigate = useNavigate();
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [hasMore, setHasMore] = useState(false);
  const offsetRef = useRef(0);
  const [loadingMore, setLoadingMore] = useState(false);

  const fetchList = useCallback(async ({ reset = true } = {}) => {
    try {
      if (reset) {
        offsetRef.current = 0;
        setLoading(true);
      }
      const response = await axios.get(`${API}/ffid`, {
        params: {
          limit: TICKETS_PAGE_LIMIT,
          offset: reset ? 0 : offsetRef.current,
          include_total: reset ? true : undefined,
        },
      });
      const page = normalizeTicketsListPayload(response.data);
      if (reset) {
        setTickets(page.items);
        offsetRef.current = TICKETS_PAGE_LIMIT;
      } else {
        setTickets((prev) => {
          const seen = new Set(prev.map((t) => t.ticket_number));
          return [
            ...prev,
            ...page.items.filter((t) => t?.ticket_number && !seen.has(t.ticket_number)),
          ];
        });
        offsetRef.current += TICKETS_PAGE_LIMIT;
      }
      setHasMore(page.has_more);
    } catch (error) {
      console.error('Error fetching FFID tickets:', error);
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, []);

  useEffect(() => {
    fetchList({ reset: true });
    const tick = () => {
      if (typeof document !== 'undefined' && document.visibilityState === 'hidden') return;
      fetchList({ reset: true });
    };
    const interval = setInterval(tick, POLL_MS);
    return () => clearInterval(interval);
  }, [fetchList]);

  const loadMore = async () => {
    if (loadingMore || !hasMore) return;
    setLoadingMore(true);
    await fetchList({ reset: false });
  };

  return (
    <div className="space-y-6" data-testid="ffid-tickets-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-semibold text-cp-fg flex items-center gap-2">
            <Shield className="h-6 w-6" />
            Firefighter ID
          </h1>
          <p className="text-sm text-cp-muted mt-1">
            FFID workflow tickets for operators with <code className="text-xs">ffid.update</code>.
          </p>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={() => fetchList({ reset: true })}
          data-testid="ffid-tickets-refresh"
        >
          <RefreshCw className="h-4 w-4 mr-2" />
          Refresh
        </Button>
      </div>

      {loading ? (
        <Card>
          <CardContent className="py-12 text-center text-cp-muted">Loading FFID tickets…</CardContent>
        </Card>
      ) : tickets.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center text-cp-muted" data-testid="ffid-tickets-empty">
            No Firefighter ID tickets found.
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4">
          {tickets.map((ticket) => (
            <Card
              key={ticket.id || ticket.ticket_number}
              className="cursor-pointer hover:-translate-y-px transition-transform"
              onClick={() => navigate(`/ffid/${ticket.ticket_number}`)}
              data-testid={`ffid-ticket-card-${ticket.ticket_number}`}
            >
              <CardHeader>
                <div className="flex justify-between items-start gap-3">
                  <div>
                    <div className="flex items-center gap-2 mb-2 flex-wrap">
                      <Badge variant="outline" className="font-mono text-xs">
                        {ticket.ticket_number}
                      </Badge>
                      {ticket.workflow_stage ? (
                        <Badge className={stageBadgeClass(ticket.workflow_stage, ticket)}>
                          {getWorkflowStageLabel(ticket) || ticket.workflow_stage}
                        </Badge>
                      ) : null}
                      {isPendingApproval(ticket) ? (
                        <Badge className="bg-cp-imp text-white">Pending approval</Badge>
                      ) : null}
                    </div>
                    <CardTitle className="text-base">{ticket.title}</CardTitle>
                    <CardDescription className="mt-1">
                      {ticket.requester_name} · {ticket.sap_system}
                    </CardDescription>
                  </div>
                </div>
              </CardHeader>
            </Card>
          ))}
        </div>
      )}

      {hasMore ? (
        <div className="flex justify-center">
          <Button variant="secondary" onClick={loadMore} disabled={loadingMore}>
            {loadingMore ? 'Loading…' : 'Load more'}
          </Button>
        </div>
      ) : null}
    </div>
  );
}
